<template>
  <fragment>
    <SectionHeader :companySelect="false">
      <template v-slot:breadcrumbs>
        <h1>
          <Link to="/admin/users">Admin</Link>
          {{ $t('companies.users') }}
        </h1>
      </template>
      <template v-slot:meta>
        <Link to="/admin/users/new" class="button button-green button-round button-small">{{
          $t('companies.addUser')
        }}</Link>
      </template>
    </SectionHeader>

    <div class="body-content">
      <admin-nav></admin-nav>
      <div class="body">
        <LoadingSpinner v-if="loading" full />
        <div v-else>
          <SearchField
            :label="$t('admin.user.searchUser')"
            :name="'user-search-input'"
            :placeholder="$t('admin.user.searchPlaceholder')"
            v-model="searchPhrase"
            @input="searchHandler"
          />
          <div class="content-wrap">
            <div class="content-body">
              <Table>
                <template #header>
                  <SortableColumnHeader
                    :label="$t('admin.user.name')"
                    :sortOrder="'ascending'"
                    :sortValue="'last_name'"
                    @sortColumn="sortColumn"
                  />
                  <SortableColumnHeader :label="$t('admin.user.email')" :sortValue="'email'" @sortColumn="sortColumn" />
                  <th>{{ $t('admin.user.company') }}</th>
                  <th class="table__th-actions">{{ $t('meta.actions') }}</th>
                </template>
                <template #body>
                  <tr v-for="{ uuid, first_name, last_name, email, companies } in users" :key="uuid">
                    <td>{{ first_name ? first_name : '-' }} {{ last_name ? last_name : '-' }}</td>
                    <td>{{ email }}</td>
                    <td>{{ displayUserCompany(companies) }}</td>
                    <td class="table__actions">
                      <Link :to="`/admin/users/${uuid}`" class="table__action-button">
                        <span class="material-icons">edit</span>
                      </Link>
                      <button @click="deleteUser(uuid)" class="table__action-button">
                        <span class="material-icons">delete</span>
                      </button>
                    </td>
                  </tr>
                </template>
              </Table>
            </div>
          </div>

          <div v-if="pagination && pagination.totalObjects > 0" class="pagination">
            <pagination
              :records="pagination.totalObjects"
              v-model="pagination.page"
              :per-page="pagination.perPage"
              @paginate="getUsers"
              :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            />
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<script>
import { Fragment } from 'vue-fragment'
import Pagination from 'vue-pagination-2'

import SearchField from '@/components/Form/Search/SearchField'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table'
import SortableColumnHeader from '@/components/Table/SortableColumnHeader'
import AdminNav from '@/components/admin_nav'
import stringUtils from '@/components/mixins/stringUtils'

import adminsApi from '@/api/admin'

export default {
  name: 'admin_users',
  inject: ['showPopup'],
  components: {
    AdminNav,
    Fragment,
    LoadingSpinner,
    Pagination,
    SearchField,
    SectionHeader,
    SortableColumnHeader,
    Table,
  },
  mixins: [stringUtils],
  data() {
    return {
      loading: true,
      users: [],
      searchPhrase: '',
      pagination: {
        page: 1,
        totalObjects: 0,
        perPage: 0,
      },
      sortOrder: 'ascending',
    }
  },
  methods: {
    searchHandler() {
      this.getUsers(1)
    },
    async getUsers(page) {
      const currentPage = page || this.pagination.page
      this.loading = true
      const params = { page: currentPage }
      if (this.searchPhrase.length > 0) {
        params['search_phrase'] = this.searchPhrase
      }
      const result = await adminsApi.getUsers(params)
      if (!result) {
        this.loading = false
        return
      }
      this.users = result.users.sort((a, b) => this.compareLocaleString(a.last_name, b.last_name, this.sortOrder))
      this.pagination.page = currentPage
      this.pagination.totalObjects = result.pagination?.total_objects
      this.pagination.perPage = result.pagination?.per_page
      this.loading = false
    },
    async deleteUser(uuid) {
      if (!(await this.showPopup({ content: this.$t('admin.user.deleteUserConfirmation'), type: 'confirm' }))) return

      adminsApi.deleteUser(uuid).then(() => this.getUsers())
    },
    sortColumn(order, value) {
      this.users = this.users.sort((a, b) => this.compareLocaleString(a[value], b[value], order))
    },
    displayUserCompany(companies) {
      if (companies.length === 1) {
        return companies[0].company?.name
      }
      return `${companies.length} ${this.$t('meta.companies')}`
    },
  },
  mounted() {
    this.getUsers()
    this.loading = false
  },
}
</script>

<style scoped lang="scss">
.flex-container {
  padding: 1.25rem;
}
</style>
